<script setup lang="ts">
import { slugify } from '~~/shared/utilities/slugify';

const activeSpeakerGradientsSvg = '/img/2026/speakers/active-speaker-gradients.svg';
const glowEllipse1Svg = '/img/2026/speakers/glow-ellipse-1.svg';
const glowEllipse4Svg = '/img/2026/speakers/glow-ellipse-4.svg';
const shardBottomPng = '/img/2026/speakers/desktop-bottom-shard.png';
const mobileBottomShardPng = '/img/2026/speakers/mobile-botom-shard.png';
const topShardPng = '/img/2026/speakers/top-shared.png';
const speaker1Png = '/img/2026/speakers/speaker-1.png';
const speaker2Png = '/img/2026/speakers/speaker-2.png';
const speaker3Png = '/img/2026/speakers/speaker-3.png';
const speaker4Png = '/img/2026/speakers/speaker-4.png';
const speakerThumbPng = '/img/2026/speakers/speaker-thumb.png';
const logoUnSvg = '/img/2026/speakers/logo-un.svg';
const logoHotOnesSvg = '/img/2026/speakers/logo-hot-ones.svg';
const logoHubspotSvg = '/img/2026/speakers/logo-hubspot.svg';

interface BuilderSpeaker {
    name?: string;
    title?: string;
    bio?: string;
    image?: { src?: string; altText?: string };
    sponsorLogo?: { src?: string; altText?: string };
}

interface ButtonInput {
    label?: string;
    title?: string;
    url?: string;
}

const props = withDefaults(defineProps<{
    headline?: string;
    subHead?: string;
    copy?: string;
    button?: ButtonInput;
    eyebrowText?: string;
    speakers?: BuilderSpeaker[];
    lazyLoad?: boolean;
    treatAsHero?: boolean;
}>(), {
    headline: '',
    lazyLoad: true,
});

const defaultPhotos = [speaker1Png, speaker2Png, speaker3Png, speaker4Png, speaker1Png];

const defaultSpeakers = [
    {
        name: 'Colin Jost',
        role: 'Writer & Comedian @ Saturday Night Live',
        photo: speaker1Png,
        logo: speakerThumbPng,
        logoAlt: 'SNL',
        logoType: 'image',
        pillWidth: 77,
        sessions: [
            'Thriving in Discomfort: How Being Uncomfortable Fuels Growth',
            'Email Teardown Live! You Can\'t Handle the Truth!',
            'The Everything Email Marketing Session',
        ],
    },
    {
        name: 'Amy Poehler',
        role: 'Actress, Comedian & Producer',
        photo: speaker2Png,
        logo: logoUnSvg,
        logoAlt: 'Unilever',
        logoType: 'svg',
        pillWidth: 71,
        sessions: [
            'Finding Joy in Every Campaign',
            'The Power of Yes, And in Marketing',
        ],
    },
    {
        name: 'Sean Evans',
        role: 'Host @ Hot Ones',
        photo: speaker3Png,
        logo: logoHotOnesSvg,
        logoAlt: 'Hot Ones',
        logoType: 'svg',
        pillWidth: 106,
        sessions: [
            'The Art of the Interview: Building Authentic Connections',
            'Viral Content That Actually Converts',
        ],
    },
    {
        name: 'Kipp Bodnar',
        role: 'CMO @ HubSpot',
        photo: speaker4Png,
        logo: logoHubspotSvg,
        logoAlt: 'HubSpot',
        logoType: 'svg',
        pillWidth: 93,
        sessions: [
            'The State of Marketing in 2026',
            'AI-Powered Growth Strategies',
        ],
    },
    {
        name: 'Tina Fey',
        role: 'Actress, Writer & Producer',
        photo: speaker1Png,
        logo: speakerThumbPng,
        logoAlt: 'SNL',
        logoType: 'image',
        pillWidth: 77,
        sessions: [
            'Leading with Humor: Lessons from 30 Rock',
            'Writing Your Way to the Top',
        ],
    },
    {
        name: 'Gary Vaynerchuk',
        role: 'CEO @ VaynerMedia',
        photo: speaker2Png,
        logo: logoUnSvg,
        logoAlt: 'VaynerMedia',
        logoType: 'svg',
        pillWidth: 71,
        sessions: [
            'Social Media in 2026: What Actually Works',
            'Building Brands That Last',
        ],
    },
    {
        name: 'Brené Brown',
        role: 'Research Professor & Author',
        photo: speaker3Png,
        logo: logoHotOnesSvg,
        logoAlt: 'Brené Brown',
        logoType: 'svg',
        pillWidth: 106,
        sessions: [
            'Vulnerability as a Leadership Superpower',
            'Courage in the Workplace',
        ],
    },
    {
        name: 'Yamini Rangan',
        role: 'CEO @ HubSpot',
        photo: speaker4Png,
        logo: logoHubspotSvg,
        logoAlt: 'HubSpot',
        logoType: 'svg',
        pillWidth: 93,
        sessions: [
            'The Future of Customer Platforms',
            'Scaling with Purpose',
        ],
    },
    {
        name: 'Seth Godin',
        role: 'Author & Entrepreneur',
        photo: speaker1Png,
        logo: speakerThumbPng,
        logoAlt: 'Seth Godin',
        logoType: 'image',
        pillWidth: 77,
        sessions: [
            'The Practice of Shipping Creative Work',
            'Marketing in the Connection Economy',
        ],
    },
    {
        name: 'Reshma Saujani',
        role: 'Founder @ Girls Who Code',
        photo: speaker2Png,
        logo: logoUnSvg,
        logoAlt: 'Girls Who Code',
        logoType: 'svg',
        pillWidth: 71,
        sessions: [
            'Brave, Not Perfect: Redefining Success',
            'Closing the Gender Gap in Tech',
        ],
    },
    {
        name: 'Simon Sinek',
        role: 'Author & Optimist',
        photo: speaker3Png,
        logo: logoHotOnesSvg,
        logoAlt: 'Simon Sinek',
        logoType: 'svg',
        pillWidth: 106,
        sessions: [
            'Start With Why: The Golden Circle in 2026',
            'Infinite Games in Business',
        ],
    },
    {
        name: 'Dharmesh Shah',
        role: 'CTO & Co-Founder @ HubSpot',
        photo: speaker4Png,
        logo: logoHubspotSvg,
        logoAlt: 'HubSpot',
        logoType: 'svg',
        pillWidth: 93,
        sessions: [
            'AI Agents and the Future of Software',
            'Building Company Culture at Scale',
        ],
    },
    {
        name: 'Oprah Winfrey',
        role: 'Media Executive & Philanthropist',
        photo: speaker1Png,
        logo: speakerThumbPng,
        logoAlt: 'OWN',
        logoType: 'image',
        pillWidth: 77,
        sessions: [
            'The Power of Intentional Living',
            'Building an Authentic Brand',
        ],
    },
    {
        name: 'Neil deGrasse Tyson',
        role: 'Astrophysicist & Author',
        photo: speaker2Png,
        logo: logoUnSvg,
        logoAlt: 'Hayden Planetarium',
        logoType: 'svg',
        pillWidth: 71,
        sessions: [
            'Cosmic Perspectives on Innovation',
            'Science Communication in the Digital Age',
        ],
    },
    {
        name: 'Malala Yousafzai',
        role: 'Activist & Nobel Laureate',
        photo: speaker3Png,
        logo: logoHotOnesSvg,
        logoAlt: 'Malala Fund',
        logoType: 'svg',
        pillWidth: 106,
        sessions: [
            'Education as the Ultimate Growth Strategy',
            'Speaking Truth to Power',
        ],
    },
    {
        name: 'Brian Halligan',
        role: 'Co-Founder @ HubSpot',
        photo: speaker4Png,
        logo: logoHubspotSvg,
        logoAlt: 'HubSpot',
        logoType: 'svg',
        pillWidth: 93,
        sessions: [
            'The Next Chapter of Inbound',
            'Lessons from Two Decades of SaaS',
        ],
    },
];

const { data: apiSpeakers } = useLazyAsyncData('featured-speakers-lookup',
    () => $fetch('/api/v2/speakers', { params: { featured: true, year: 2026 } }),
    { server: true },
);

const speakerSlugMap = computed(() => {
    const map = new Map();
    if (apiSpeakers.value?.speakers) {
        for (const s of apiSpeakers.value.speakers) {
            map.set(s.name?.toLowerCase(), s.slug || s.id);
        }
    }
    return map;
});

const apiSpeakerList = computed(() => {
    const list = apiSpeakers.value?.speakers;
    if (!Array.isArray(list) || !list.length) return [];
    // API already sorts featured speakers by featured_order.
    return list.map((s, i) => {
        const role = [s.job_title, s.company].filter(Boolean).join(' @ ');
        const photo = s.featured_speaker_image?.[0]?.url
            || s.profile_image?.[0]?.url
            || defaultPhotos[i % defaultPhotos.length];
        const logo = s.featured_speaker_logo?.[0]?.url || '';
        return {
            id: s.slug || s.id || null,
            name: s.name || '',
            role,
            photo,
            logo,
            logoAlt: s.company || s.name || '',
            logoType: logo.endsWith('.svg') ? 'svg' : 'image',
            pillWidth: 80,
            sessions: s.bio ? [s.bio] : [],
        };
    });
});

const speakerList = computed(() => {
    if (props.speakers?.length) {
        return props.speakers.map((s, i) => ({
            id: speakerSlugMap.value.get(s.name?.toLowerCase()) || (s.name ? slugify(s.name) : null),
            name: s.name || '',
            role: s.title || '',
            photo: s.image?.src || defaultPhotos[i % defaultPhotos.length],
            logo: s.sponsorLogo?.src || '',
            logoAlt: s.sponsorLogo?.alt || s.sponsorLogo?.altText || '',
            logoType: s.sponsorLogo?.src?.endsWith('.svg') ? 'svg' : 'image',
            pillWidth: 80,
            sessions: s.bio ? [s.bio] : [],
        }));
    }
    if (apiSpeakerList.value.length) return apiSpeakerList.value;
    return defaultSpeakers;
});

const ACTIVE_SCALE_DESKTOP = 1.568; // 439 / 280
const ACTIVE_SCALE_MOBILE = 1;
const INACTIVE_SCALE_MOBILE = 0.75;
const ITEM_WIDTH = 280;

const activeIndex = ref(0);
const carouselRef = ref(null);
const sectionRef = ref(null);
const isVisible = ref(false);
const speakerScales = reactive(speakerList.value.map(() => 1));

// Keep speakerScales sized to speakerList — the API source can resize the list
// after SSR hydration, so resync the scales array length when that happens.
watch(() => speakerList.value.length, (n) => {
    if (n === speakerScales.length) return;
    speakerScales.splice(0, speakerScales.length, ...Array(n).fill(1));
});

// Get the step size (inactive width + gap) — this is the consistent distance
// between speaker left edges when snapped, since all non-active speakers are inactive width
const getStep = () => {
    const isDesktop = window.innerWidth >= 768;
    const gap = isDesktop ? 70 : 35;
    const inactiveWidth = isDesktop ? ITEM_WIDTH : ITEM_WIDTH * INACTIVE_SCALE_MOBILE;
    return inactiveWidth + gap;
};

// Calculate scroll target for a given speaker index
const getTargetScroll = (index) => {
    return index * getStep();
};

// Scroll to a speaker by index
const scrollToSpeaker = (index) => {
    clearTimeout(snapTimeout);
    springSnapTo(getTargetScroll(index), 0);
};

const hasPrev = computed(() => activeIndex.value > 0);
const hasNext = computed(() => activeIndex.value < speakerList.value.length - 1);

const prev = () => {
    if (!hasPrev.value) return;
    scrollToSpeaker(activeIndex.value - 1);
};
const next = () => {
    if (!hasNext.value) return;
    scrollToSpeaker(activeIndex.value + 1);
};

// Scroll-driven interpolation
let ticking = false;
let snapTimeout = null;
let isSnapping = false;
let isScrolling = false;
let isTouching = false;
let scrollEndTimeout = null;
let animationId = null;
let observer = null;

// Velocity tracking — EMA smoothed for natural feel
let lastScrollLeft = 0;
let lastScrollTime = 0;
let velocity = 0; // px/ms, smoothed

// Cancel a running spring animation — only acts if a spring is active
const cancelSnap = () => {
    if (!isSnapping) return;
    cancelAnimationFrame(animationId);
    clearTimeout(snapTimeout);
    isSnapping = false;
};

const updateScales = () => {
    const container = carouselRef.value;
    if (!container) return;

    const scrollLeft = container.scrollLeft;
    const step = getStep();
    const isDesktop = window.innerWidth >= 768;

    // Which speaker index are we at?
    const rawIndex = scrollLeft / step;
    const currentIdx = Math.max(0, Math.min(Math.floor(rawIndex), speakerList.value.length - 1));
    const nextIdx = Math.min(currentIdx + 1, speakerList.value.length - 1);
    const progress = Math.max(0, Math.min(rawIndex - currentIdx, 1));

    // Scale values depend on viewport
    const activeScale = isDesktop ? ACTIVE_SCALE_DESKTOP : ACTIVE_SCALE_MOBILE;
    const inactiveScale = isDesktop ? 1 : INACTIVE_SCALE_MOBILE;
    const scaleDiff = activeScale - inactiveScale;

    for (let i = 0; i < speakerList.value.length; i++) {
        if (i === currentIdx) {
            speakerScales[i] = activeScale - progress * scaleDiff;
        } else if (i === nextIdx && currentIdx !== nextIdx) {
            speakerScales[i] = inactiveScale + progress * scaleDiff;
        } else {
            speakerScales[i] = inactiveScale;
        }
    }

    // Update activeIndex based on which has larger scale
    activeIndex.value = progress < 0.5 ? currentIdx : nextIdx;
};

// Spring-based snap: carries the user's scroll velocity into a physics simulation
// that decelerates naturally toward the target — no fixed easing curve
const springSnapTo = (target, initialVelocityPxMs) => {
    const container = carouselRef.value;
    if (!container) return;

    cancelAnimationFrame(animationId);
    isSnapping = true;

    let pos = container.scrollLeft;
    if (Math.abs(target - pos) < 1 && Math.abs(initialVelocityPxMs) < 0.05) {
        container.scrollLeft = target;
        updateScales();
        isSnapping = false;
        return;
    }

    // Ignore incoming velocity — ease cleanly to target with no overshoot
    let vel = 0;

    // Spring tuning — overdamped for smooth deceleration, no bounce
    const stiffness = 0.08;
    const damping = 0.65;

    const animate = () => {
        const displacement = target - pos;

        // Spring force pulls toward target
        vel += displacement * stiffness;
        // Damping bleeds off energy each frame
        vel *= damping;
        pos += vel;

        container.scrollLeft = pos;
        updateScales();

        // Settle when close enough and nearly stopped
        if (Math.abs(displacement) < 0.5 && Math.abs(vel) < 0.3) {
            container.scrollLeft = target;
            updateScales();
            isSnapping = false;
            return;
        }

        animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
};

const onScroll = () => {
    // Smoothed velocity via exponential moving average
    const now = performance.now();
    const container = carouselRef.value;
    if (container && lastScrollTime > 0) {
        const dt = now - lastScrollTime;
        if (dt > 0 && dt < 100) {
            const instantVel = (container.scrollLeft - lastScrollLeft) / dt;
            velocity = velocity * 0.7 + instantVel * 0.3;
        }
    }
    if (container) lastScrollLeft = container.scrollLeft;
    lastScrollTime = now;

    // Track scrolling state
    isScrolling = true;
    clearTimeout(scrollEndTimeout);
    scrollEndTimeout = setTimeout(() => { isScrolling = false; }, 150);

    // Always update scales (even during our animation) for smooth interpolation
    if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
            updateScales();
            ticking = false;
        });
    }

    // Don't re-trigger snap during a programmatic scroll or touch
    if (isSnapping || isTouching) return;

    // Custom snap: when scrolling stops, settle to nearest speaker
    clearTimeout(snapTimeout);
    snapTimeout = setTimeout(() => {
        const container = carouselRef.value;
        if (!container) return;
        const step = getStep();
        const rawIndex = container.scrollLeft / step;

        // Use velocity to predict where the user intended to land
        // Project position forward by ~200ms of current velocity
        const projectedIndex = (container.scrollLeft + velocity * 200) / step;

        let snapIndex;
        if (Math.abs(velocity) > 0.2) {
            // Moving with intent — snap in the direction of velocity, but only 1 at a time on mobile
            if (window.innerWidth < 768) {
                snapIndex = velocity > 0 ? activeIndex.value + 1 : activeIndex.value - 1;
            } else {
                snapIndex = Math.round(projectedIndex);
            }
        } else {
            // Barely moving — snap to nearest
            snapIndex = Math.round(rawIndex);
        }

        const clampedIndex = Math.max(0, Math.min(snapIndex, speakerList.value.length - 1));
        springSnapTo(getTargetScroll(clampedIndex), velocity);
    }, 50);
};

const onResize = () => {
    updateScales();
    // Re-snap to the current speaker so scroll position stays aligned
    const container = carouselRef.value;
    if (container) {
        container.scrollLeft = getTargetScroll(activeIndex.value);
        updateScales();
    }
};

onMounted(() => {
    updateScales();
    window.addEventListener('resize', onResize);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        isVisible.value = true;
        return;
    }

    observer = new IntersectionObserver(
        ([entry]) => {
            if (entry.isIntersecting) {
                isVisible.value = true;
                observer.disconnect();
                observer = null;
            }
        },
        { threshold: 0.1 },
    );

    if (sectionRef.value) observer.observe(sectionRef.value);

    // Touch: cancel spring animation immediately, suppress snap until release
    const container = carouselRef.value;
    if (container) {
        let touchStartScrollLeft = 0;
        let wasAnimating = false;
        container.addEventListener('touchstart', () => {
            wasAnimating = isSnapping;
            cancelSnap();
            clearTimeout(snapTimeout);
            isTouching = true;
            // Capture scroll position AFTER cancelling animation
            touchStartScrollLeft = container.scrollLeft;
            velocity = 0;
        }, { passive: true });

        container.addEventListener('touchend', () => {
            isTouching = false;
            const delta = container.scrollLeft - touchStartScrollLeft;
            const minSwipe = 2;
            const step = getStep();
            let snapIndex;
            if (wasAnimating || Math.abs(delta) <= minSwipe) {
                // Stop tap or negligible movement — settle on nearest
                snapIndex = Math.round(container.scrollLeft / step);
            } else {
                snapIndex = delta > 0 ? activeIndex.value + 1 : activeIndex.value - 1;
            }
            snapIndex = Math.max(0, Math.min(snapIndex, speakerList.value.length - 1));
            const clampedVelocity = Math.max(-2, Math.min(2, velocity));
            springSnapTo(getTargetScroll(snapIndex), clampedVelocity);
        }, { passive: true });
    }
});

onBeforeUnmount(() => {
    cancelAnimationFrame(animationId);
    clearTimeout(snapTimeout);
    observer?.disconnect();
    window.removeEventListener('resize', onResize);
});

</script>

<template>
    <section ref="sectionRef"
        class="block-featured-speakers relative overflow-hidden isolate
        py-[60px] pl-[var(--box-padding-lr-m)] pr-0
        md:py-0 md:pl-0"
        aria-label="Featured speakers">

        <!-- Background decorative layers -->
        <div aria-hidden="true" class="absolute inset-0 z-0 pointer-events-none overflow-hidden">
            <!-- Gradient glow ellipses -->
            <img :src="glowEllipse1Svg" loading="lazy" decoding="async" alt="" class="absolute w-[668px] h-[676px] left-[12.2%] top-[56%] scale-[1.6] opacity-60" />
            <img :src="activeSpeakerGradientsSvg" loading="lazy" decoding="async" alt="" class="absolute w-[333px] h-[305px] left-[6.8%] top-[23.3%] scale-[2.2] opacity-70" />
            <img :src="glowEllipse4Svg" loading="lazy" decoding="async" alt="" class="absolute w-[516px] h-[516px] right-[7%] top-[-14.9%] scale-[1.6]" />
            <div class="hidden md:display-block absolute right-0 top-0">
                <img :src="topShardPng" loading="lazy" decoding="async" alt="" class="w-full h-auto" />
            </div>
        </div>

        <!-- Header section -->
        <div class="relative z-10 transition-opacity duration-500 ease-out
            pr-[var(--box-padding-lr-m)]
            md:px-[var(--box-padding-lr)] md:pt-[83px] md:pr-[var(--box-padding-lr)]"
            :class="isVisible ? 'opacity-100' : 'opacity-0'">
            <div class="flex flex-col gap-[14px] md:gap-[18px] max-w-[819px]">
                <div class="flex flex-col gap-[14px] items-start
                    md:flex-row md:items-center md:gap-[28px]">
                    <h2 class="font-hubspot-serif font-normal text-white normal-case mb-0
                        text-[calc(34/var(--base-fs)*1rem)] leading-[1.1]
                        md:text-[calc(60/var(--base-fs)*1rem)] md:whitespace-nowrap md:shrink-0">
                        {{ headline || 'Past featured speakers' }}
                    </h2>
                    <div v-if="button?.label" class="hidden md:display-block pt-[7px] shrink-0">
                        <a :href="button.url || '#'" :title="button.title"
                            class="inline-flex items-center gap-2.5 px-5 py-2.5
                            rounded-[50px] border border-(--pink)
                            text-(--pink) no-underline
                            font-hubspot-serif text-[calc(17/var(--base-fs)*1rem)] leading-[1.4] tracking-[0.34px]
                            hover:bg-white hover:text-(--magenta) hover:border-white
                            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--pink)
                            transition-colors duration-200">
                            {{ button.label }}
                        </a>
                    </div>
                </div>
                <div v-if="copy" class="font-hubspot-sans font-light text-white normal-case
                    text-[calc(16/var(--base-fs)*1rem)] leading-[26px] tracking-[0.32px]"
                    v-html="copy" />
                <p v-else class="font-hubspot-sans font-light text-white normal-case
                    text-[calc(16/var(--base-fs)*1rem)] leading-[26px] tracking-[0.32px]">
                    Get to know the entrepreneurs, strategists, and industry pioneers.
                </p>
            </div>
        </div>

        <!-- Speaker carousel stage -->
        <div ref="carouselRef"
            class="speaker-carousel relative z-10 mt-[50px]
                flex items-end gap-[35px] md:gap-[70px]
                overflow-x-auto
                h-[280px] md:h-[439px]
                -ml-[var(--box-padding-lr-m)] pl-[var(--box-padding-lr-m)]
                md:ml-0 md:pl-[var(--box-padding-lr)]"
            @scroll="onScroll"
            @pointerdown="cancelSnap"
            @wheel="cancelSnap">
            <div v-for="(speaker, idx) in speakerList" :key="idx"
                class="speaker-item relative shrink-0
                    transition-opacity duration-500 ease-out"
                :class="isVisible ? 'opacity-100' : 'opacity-0'"
                :style="{
                    width: `${speakerScales[idx] * ITEM_WIDTH}px`,
                    transitionDelay: isVisible ? `${idx * 80}ms` : '0ms',
                }">
                <button
                    class="group relative cursor-pointer bg-transparent border-none p-0 display-block w-full
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pink)] focus-visible:ring-offset-2
                        focus-visible:ring-offset-transparent rounded-lg
                        transition-opacity duration-300"
                    :class="idx !== activeIndex ? 'hover:opacity-90' : ''"
                    :aria-label="idx === activeIndex ? `Current speaker: ${speaker.name}` : `View ${speaker.name}`"
                    :aria-current="idx === activeIndex ? 'true' : undefined"
                    @click="!(isSnapping || isScrolling) && (idx === activeIndex && speaker.id ? navigateTo(`/speakers/${speaker.id}`) : scrollToSpeaker(idx))">
                    <MessImage :key="speaker.photo" :image="{ src: speaker.photo, alt: speaker.name }" lazy
                        class="w-full h-auto transition-[filter] duration-300 group-hover:brightness-125" />
                </button>
                <!-- Logo pill (decorative — interaction handled by photo button) -->
                <div v-if="speaker.logo"
                    class="speaker-pill absolute bottom-0
                        flex items-center justify-center
                        rounded-[50px] bg-white overflow-hidden border-none
                        h-[40px] md:h-[50px] min-w-[60px] px-[14px] md:px-[18px]
                        pointer-events-none
                        transition-all duration-300"
                    :class="idx === activeIndex ? 'right-[45px] md:right-[90px]' : 'right-[45px]'"
                    aria-hidden="true">
                    <img :src="speaker.logo" :alt="speaker.logoAlt || ''" loading="lazy" decoding="async"
                        class="w-auto max-h-[33px] max-w-full object-contain" />
                </div>
            </div>
            <!-- Spacer so last speaker can scroll into active position -->
            <div aria-hidden="true" class="shrink-0 w-[calc(100vw-280px-var(--box-padding-lr-m)-16px)] md:w-[calc(100vw-439px-var(--box-padding-lr)-20px)]"></div>
        </div>

        <!-- Speaker details + navigation -->
        <div class="relative z-10 mt-[32px] pb-[60px] pr-[var(--box-padding-lr-m)] transition-opacity duration-500 ease-out
            md:px-[var(--box-padding-lr)] md:mt-[43px] md:pb-[140px]"
            :class="isVisible ? 'opacity-100' : 'opacity-0'"
            :style="{ transitionDelay: isVisible ? '300ms' : '0ms' }">
            <div class="flex items-start justify-between">
                <!-- Speaker bios — all rendered, grid-overlapped, tallest sets height -->
                <div class="grid w-full md:max-w-[555px]">
                    <div v-for="(speaker, idx) in speakerList" :key="idx"
                        class="col-start-1 row-start-1 flex flex-col gap-[24px] transition-all duration-300 ease-out"
                        :class="idx === activeIndex
                            ? 'opacity-100 translate-x-0'
                            : 'opacity-0 pointer-events-none translate-x-[20px]'"
                        :aria-hidden="idx !== activeIndex">
                        <div class="flex flex-col gap-[17px]">
                            <h3 class="font-hubspot-serif font-normal text-white normal-case mb-0
                                text-[calc(36/var(--base-fs)*1rem)] leading-[1.2]
                                md:text-[calc(36/var(--base-fs)*1rem)]">
                                <NuxtLink v-if="speaker.id" :to="`/speakers/${speaker.id}`"
                                    class="text-white no-underline hover:underline
                                        decoration-dotted decoration-[var(--pink)] decoration-[3.5%]
                                        underline-offset-[5px]
                                        [text-decoration-skip-ink:auto]
                                        [text-underline-position:from-font]">
                                    {{ speaker.name }}
                                </NuxtLink>
                                <template v-else>{{ speaker.name }}</template>
                            </h3>
                            <p class="font-hubspot-sans font-medium text-white uppercase
                                text-[calc(12/var(--base-fs)*1rem)] leading-[1.5] tracking-[0.84px] mb-0">
                                {{ speaker.role }}
                            </p>
                        </div>
                        <div class="flex flex-col gap-[14px] md:gap-[18px] font-hubspot-sans font-light text-[var(--pink)]
                            text-[calc(15/var(--base-fs)*1rem)] leading-[24px] tracking-[0.3px] speaker-bio-content"
                            v-for="(session, sIdx) in speaker.sessions" :key="sIdx"
                            v-html="session" />
                    </div>
                </div>

                <!-- Navigation arrows -->
                <div class="hidden md:flex items-center gap-[16px] border border-[var(--pink)] rounded-full w-[76px] h-[42px] px-[16px] shrink-0 justify-center">
                    <button
                        class="shrink-0 flex items-center justify-center
                            text-[var(--pink)] cursor-pointer bg-transparent border-none p-0
                            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pink)] rounded-sm
                            transition-opacity duration-200"
                        :class="hasPrev ? 'opacity-100 hover:opacity-70' : 'opacity-30 pointer-events-none'"
                        :aria-disabled="!hasPrev"
                        aria-label="Previous speaker"
                        @click="prev">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                            <path d="M13 7H1M1 7L7.14 0.5M1 7L7.14 13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                    <button
                        class="shrink-0 flex items-center justify-center
                            text-[var(--pink)] cursor-pointer bg-transparent border-none p-0
                            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pink)] rounded-sm
                            transition-opacity duration-200"
                        :class="hasNext ? 'opacity-100 hover:opacity-70' : 'opacity-30 pointer-events-none'"
                        :aria-disabled="!hasNext"
                        aria-label="Next speaker"
                        @click="next">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                            <path d="M1 7H13M13 7L6.86 0M13 7L6.86 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
            </div>
        </div>

        <!-- Mobile navigation + More Details -->
        <div class="relative z-10 flex md:hidden items-center gap-[16px] pr-[var(--box-padding-lr-m)]">
            <div class="flex items-center gap-[16px] border border-[var(--pink)] rounded-full w-[76px] h-[42px] px-[16px] justify-center">
                <button
                    class="shrink-0 flex items-center justify-center
                        text-[var(--pink)] cursor-pointer bg-transparent border-none p-0
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pink)] rounded-sm
                        transition-opacity duration-200"
                    :class="hasPrev ? 'opacity-100' : 'opacity-30 pointer-events-none'"
                    :aria-disabled="!hasPrev"
                    aria-label="Previous speaker"
                    @click="prev">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                        <path d="M13 7H1M1 7L7.14 0.5M1 7L7.14 13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>
                <button
                    class="shrink-0 flex items-center justify-center
                        text-[var(--pink)] cursor-pointer bg-transparent border-none p-0
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pink)] rounded-sm
                        transition-opacity duration-200"
                    :class="hasNext ? 'opacity-100' : 'opacity-30 pointer-events-none'"
                    :aria-disabled="!hasNext"
                    aria-label="Next speaker"
                    @click="next">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                        <path d="M1 7H13M13 7L6.86 0M13 7L6.86 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>
            </div>
            <MessLink v-if="button?.label" :href="button.url || '#'"
                class="inline-flex items-center gap-[10px] px-[20px] py-[10px]
                    rounded-[50px] border border-[var(--pink)]
                    text-[var(--pink)] no-underline
                    font-hubspot-serif text-[calc(16/var(--base-fs)*1rem)] leading-[1.3] tracking-[0.32px]
                    hover:bg-white hover:text-[var(--magenta)] hover:border-white
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pink)]
                    transition-colors duration-200">
                {{ button.label }}
            </MessLink>
        </div>

        <!-- Bottom shard — desktop -->
        <img :src="shardBottomPng" loading="lazy" decoding="async" alt="" aria-hidden="true"
            class="hidden md:display-block absolute -bottom-[60px] right-0 w-[1100px] h-auto pointer-events-none z-0" />
        <!-- Bottom shard — mobile -->
        <img :src="mobileBottomShardPng" loading="lazy" decoding="async" alt="" aria-hidden="true"
            class="md:hidden absolute bottom-0 right-0 w-[250px] h-auto pointer-events-none z-0" />
    </section>
</template>

<style>
.block-featured-speakers {
    background: linear-gradient(127.59deg, var(--magenta) 32%, var(--purple) 105.28%);
    -webkit-touch-callout: none;
    user-select: none;
}

.block-featured-speakers img {
    -webkit-touch-callout: none;
    pointer-events: none;
}

/* Carousel — native scroll, custom JS snap */
.speaker-carousel {
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
}

.speaker-carousel::-webkit-scrollbar {
    display: none;
}

/* Bio content links */
.speaker-bio-content a {
    color: white;
    text-decoration: underline;
    text-decoration-style: dotted;
    text-decoration-color: var(--pink);
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;
    transition: color 0.2s, text-decoration-style 0.2s;
}

.speaker-bio-content a:hover {
    color: var(--pink);
    text-decoration-style: solid;
}

/* Respect reduced motion */
@media (prefers-reduced-motion: reduce) {
    .block-featured-speakers * {
        transition: none !important;
        animation: none !important;
    }
}
</style>
